/**
 * @file     MatchExplanation.tsx
 * @readme   ./README.md
 * @purpose  Match-explanation card for /properties/[key]. Only renders when URL has ?explain=1. Calls /api/mls/match via secureFetch, finds this listing in the response, renders matched edges or failed-requirement reasons.
 */

'use client';

import { layoutTokens } from '@/design-system/tokens/layout';
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { alpha, useTheme } from '@mui/material/styles';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { useAnalytics } from '@/hooks/useAnalytics';
import { propertyDetailContent as C } from './pageContent';

// TODO(auth): replace with the authenticated user's ID once auth is wired into /properties/[key].
const DEMO_USER_ID = 'demo-user-id';

type Props = {
  listingKey: string;
  city: string | null;
  propertyType: string;
  bedrooms: number | null;
  baths: number | null;
  listPrice: number | null;
};

type MatchedEdge = {
  kind: string;
  user: { type: string; id?: string; label: string };
  property: { type: string; id?: string; label: string };
  alignment: number;
  weight: number;
  reason: string;
};

type MatchResult = {
  listingKey: string;
  rank: number;
  compositeScore: number;
  subgraph: { edges: MatchedEdge[]; failedRequirements: MatchedEdge[] };
};

type MatchResponse = {
  results: MatchResult[];
  candidateCount: number;
  durationMs: number;
  rerankerVersion: string;
};

export function MatchExplanation({
  listingKey,
  city,
  propertyType,
  bedrooms,
  baths,
  listPrice,
}: Props) {
  const params = useSearchParams();
  const explain = params?.get('explain') === '1';

  const { t } = useTranslation();
  const theme = useTheme();
  const { trackEvent } = useAnalytics();

  const [state, setState] = useState<
    | { phase: 'idle' }
    | { phase: 'loading' }
    | { phase: 'matched'; edges: MatchedEdge[] }
    | { phase: 'failed'; failed: MatchedEdge[] }
    | { phase: 'empty' }
    | { phase: 'error' }
  >({ phase: 'idle' });

  useEffect(() => {
    if (!explain) return;
    let cancelled = false;
    setState({ phase: 'loading' });

    const priceBand = listPrice
      ? { priceMin: Math.floor(listPrice * 0.7), priceMax: Math.ceil(listPrice * 1.3) }
      : {};

    const body = {
      userId: DEMO_USER_ID,
      filters: {
        ...priceBand,
        ...(bedrooms != null ? { bedsMin: Math.max(0, bedrooms - 1) } : {}),
        ...(baths != null ? { bathsMin: Math.max(0, baths - 1) } : {}),
        ...(propertyType ? { propertyTypes: [propertyType] } : {}),
        ...(city ? { cities: [city] } : {}),
      },
      limit: 50,
    };

    (async () => {
      try {
        const res = await secureFetch('/api/mls/match', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          if (!cancelled) setState({ phase: 'error' });
          return;
        }
        const data = (await res.json()) as MatchResponse;
        const hit = data.results.find((r) => r.listingKey === listingKey);
        if (!hit) {
          if (!cancelled) setState({ phase: 'empty' });
          return;
        }
        if (hit.subgraph.failedRequirements.length > 0) {
          if (!cancelled) setState({ phase: 'failed', failed: hit.subgraph.failedRequirements });
          return;
        }
        if (!cancelled) setState({ phase: 'matched', edges: hit.subgraph.edges });
      } catch {
        if (!cancelled) setState({ phase: 'error' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [explain, listingKey, city, propertyType, bedrooms, baths, listPrice]);

  if (!explain) return null;

  const title = t(C.match.title.key, { defaultValue: C.match.title.defaultValue });

  const onCardClick = () => {
    trackEvent({
      eventName: 'mls_match_explanation_expanded',
      eventType: 'click',
      category: 'mls',
      label: 'properties_mls_detail_match',
      pagePath: `/properties/${listingKey}`,
      metadata: { listingKey, city: city ?? undefined },
    });
  };

  return (
    <Box
      onClick={onCardClick}
      sx={{
        my: 4,
        p: 3,
        borderRadius: `${layoutTokens.radius.lg}px`,
        border: '1px solid',
        borderColor: alpha(theme.palette.text.primary, 0.1),
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {title}
      </Typography>

      {state.phase === 'loading' ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t(C.match.loading.key, { defaultValue: C.match.loading.defaultValue })}
        </Typography>
      ) : null}

      {state.phase === 'matched' ? (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {state.edges.map((e, i) => (
            <Chip
              key={`${e.kind}-${i}`}
              label={e.reason || `${e.kind}: ${e.user.label} ↔ ${e.property.label}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          ))}
        </Stack>
      ) : null}

      {state.phase === 'failed' ? (
        <Box>
          <Typography variant="body2" sx={{ color: 'warning.main', mb: 1 }}>
            {t(C.match.notMatched.key, { defaultValue: C.match.notMatched.defaultValue })}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {state.failed.map((e, i) => (
              <Chip
                key={`failed-${i}`}
                label={e.reason || e.user.label}
                size="small"
                color="warning"
                variant="outlined"
              />
            ))}
          </Stack>
        </Box>
      ) : null}

      {state.phase === 'empty' ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t(C.match.empty.key, { defaultValue: C.match.empty.defaultValue })}
        </Typography>
      ) : null}

      {state.phase === 'error' ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t(C.match.error.key, { defaultValue: C.match.error.defaultValue })}
        </Typography>
      ) : null}
    </Box>
  );
}
